import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { ChatTransport, UIMessage, UIMessageChunk } from 'ai';
import {
    AndieChatStreamDocument,
    type AndieChatStreamSubscription,
} from '@/graphql/generated/andie-chat-stream.generated';
import {
    AndiePublicChatStreamDocument,
    type AndiePublicChatStreamSubscription,
} from '@/graphql/generated/andie-public-chat-stream.generated';

const STREAM_IDLE_TIMEOUT_MS = 11 * 60 * 1000;

interface ApolloChatTransportOptions {
    client: ApolloClient<NormalizedCacheObject>;
    publicMode?: boolean;
    getBody?: () => Record<string, unknown>;
    getUserLanguage?: () => string | undefined;
}

type SubscriptionResult = AndieChatStreamSubscription | AndiePublicChatStreamSubscription;

function extractChunk(result: SubscriptionResult) {
    if ('andieChatStream' in result) return result.andieChatStream;
    if ('andiePublicChatStream' in result) return result.andiePublicChatStream;
    return undefined;
}

function hasStreamData(data: unknown): data is UIMessageChunk {
    if (data == null) return false;
    if (typeof data !== 'object') return false;
    return Object.keys(data).length > 0;
}

export class ApolloChatTransport implements ChatTransport<UIMessage> {
    private client: ApolloClient<NormalizedCacheObject>;
    private publicMode: boolean;
    private getBody: () => Record<string, unknown>;
    private getUserLanguage: () => string | undefined;
    private activeSubscription: { unsubscribe(): void } | null = null;

    constructor({
        client,
        publicMode = false,
        getBody = () => ({}),
        getUserLanguage = () => undefined,
    }: ApolloChatTransportOptions) {
        this.client = client;
        this.publicMode = publicMode;
        this.getBody = getBody;
        this.getUserLanguage = getUserLanguage;
    }

    setUserLanguageGetter(getUserLanguage: () => string | undefined) {
        this.getUserLanguage = getUserLanguage;
    }

    private createSubscriptionStream(
        variables: Record<string, unknown>,
        abortSignal?: AbortSignal,
    ): ReadableStream<UIMessageChunk> {
        return new ReadableStream({
            start: (controller) => {
                let idleTimer: ReturnType<typeof setTimeout> | null = null;
                let closed = false;
                let apolloSubscription: { unsubscribe(): void } | null = null;

                const closeStream = () => {
                    if (closed) return;
                    closed = true;
                    if (idleTimer) clearTimeout(idleTimer);
                    apolloSubscription?.unsubscribe();
                    if (this.activeSubscription === apolloSubscription) {
                        this.activeSubscription = null;
                    }
                    try {
                        controller.close();
                    } catch {
                        /* already closed */
                    }
                };

                const errorStream = (err: unknown) => {
                    if (closed) return;
                    closed = true;
                    if (idleTimer) clearTimeout(idleTimer);
                    apolloSubscription?.unsubscribe();
                    if (this.activeSubscription === apolloSubscription) {
                        this.activeSubscription = null;
                    }
                    try {
                        controller.error(err);
                    } catch {
                        /* already errored/closed */
                    }
                };

                const resetIdleTimer = () => {
                    if (idleTimer) clearTimeout(idleTimer);
                    idleTimer = setTimeout(() => {
                        apolloSubscription?.unsubscribe();
                        errorStream(new Error('Stream idle timeout'));
                    }, STREAM_IDLE_TIMEOUT_MS);
                };

                resetIdleTimer();

                apolloSubscription = this.activeSubscription = this.client
                    .subscribe<SubscriptionResult>({
                        query: this.publicMode
                            ? AndiePublicChatStreamDocument
                            : AndieChatStreamDocument,
                        variables,
                    })
                    .subscribe({
                        next: (result) => {
                            const chunk = result.data ? extractChunk(result.data) : undefined;
                            if (!chunk) return;

                            resetIdleTimer();

                            if (!closed && hasStreamData(chunk.data)) {
                                controller.enqueue(chunk.data as UIMessageChunk);
                            }

                            if (chunk.done) {
                                closeStream();
                            }
                        },
                        error: (err) => {
                            errorStream(err);
                        },
                        complete: () => {
                            closeStream();
                        },
                    });

                abortSignal?.addEventListener('abort', () => {
                    apolloSubscription?.unsubscribe();
                    closeStream();
                });
            },
        });
    }

    async sendMessages({
        chatId,
        messages,
        abortSignal,
    }: {
        trigger: 'submit-message' | 'regenerate-message';
        chatId: string;
        messageId: string | undefined;
        messages: UIMessage[];
        abortSignal: AbortSignal | undefined;
    }): Promise<ReadableStream<UIMessageChunk>> {
        this.activeSubscription?.unsubscribe();
        this.activeSubscription = null;

        const body = this.getBody();
        const latestMessage = messages[messages.length - 1];
        const threadId = (body.threadId as string | null | undefined) || chatId;
        const variables: Record<string, unknown> = {
            messages: latestMessage ? ([latestMessage] as unknown as object) : [],
            agentId: (body.agentId as string) || undefined,
            chatId: threadId,
            threadId,
        };
        const userLanguage = this.getUserLanguage();
        if (userLanguage) {
            variables.userLanguage = userLanguage;
        }
        const mode = body.mode as string | undefined;
        if (mode) {
            variables.mode = mode;
        }
        return this.createSubscriptionStream(variables, abortSignal);
    }

    async reconnectToStream(): Promise<ReadableStream<UIMessageChunk> | null> {
        return null;
    }
}
